import { AxiosResponse } from 'axios'
import { i18n } from 'i18next'
import _ from 'lodash'
import moment from 'moment'
import { all, call, getContext, put, select, spawn, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'
import { parseTranslations as briqCommonParseTranslations } from '@briq/shared'
import { i18n as BriqCommonI18n } from '@briq/shared'

import { apiCall } from '../../utils/api'
import { autoLogout } from '../../utils/autoLogout'
import { mapActionError } from '../../utils/helpers'
import { UserProfileData } from '../account/user'
import { DialogActions } from '../dialog'
import { LogActions } from '../logs'
import { RegistrationChannel, UserActions } from '../user'
import { initializeGTM } from '../gtm'
import {
  getPreferedLanguage,
  getRegistrationChannel,
  getUserHash,
  isProbeUser,
  isUserLoggedIn
} from '../user/selectors'
import { parseGetParams, translateCurrentRoute } from '../../utils/routes'

import { ConfigActions } from './actions'
import { ContentConfigurationResponse, mapContentConfigurationResponse } from './mappers'
import { getGtmId } from './selectors'
import { ConfigLoadingState } from './types'

function parseTranslations(translations: BriqCommonI18n.ApiTranslations) {
  return briqCommonParseTranslations(translations)
}

export function addTranslations(translationBundles: any, i18n: i18n) {
  _.forEach(_.keys(translationBundles), namespace => {
    i18n.addResourceBundle(i18n.language, namespace, translationBundles[namespace])
  })
}

export function* initializeTagManager() {
  const gtmId: string | undefined = yield select(getGtmId)
  const userHash: string | undefined = yield select(getUserHash)
  const registrationChannel: RegistrationChannel = yield select(getRegistrationChannel)
  const isLoggedIn: boolean = yield select(isUserLoggedIn)
  const isProbe: boolean = yield select(isProbeUser)

  if (gtmId && (!isLoggedIn || !isProbe)) {
    initializeGTM(gtmId)
    if (isLoggedIn) {
      const dataLayer = (window as any).dataLayer
      dataLayer &&
        dataLayer.push({
          userId: userHash,
          userType: registrationChannel == RegistrationChannel.BRQ ? '0' : '1'
        })
    }
  }
}

export function* checkIfUserHasToAcceptPolicies() {
  const [res1, res2]: [
    AxiosResponse<UserProfileData>,
    AxiosResponse<{ data: { codeName: string; updatedAt: string }[] }>
  ] = yield all([
    call(apiCall, {
      url: '/api/users/profile',
      method: 'get'
    }),
    call(apiCall, {
      url: '/api/content/RegistrationLegalPolicies',
      method: 'get',
      params: {
        additionalAcceptance: true
      },
      umbracoCall: true
    })
  ])

  if (
    _.some(res2.data.data, x => {
      if (_.isEmpty(x.updatedAt)) return false
      const acceptanceDate = _.find(res1.data.crifAccount.policiesAcceptance, y => y.name === x.codeName)
      if (!acceptanceDate || _.isEmpty(acceptanceDate)) return true
      return new Date(x.updatedAt) > new Date(acceptanceDate.value ? moment.utc(`${acceptanceDate.value}`).toDate() : 0)
    })
  ) {
    yield put(
      DialogActions.showDialog({
        dialog: 'PoliciesDialog'
      })
    )
  }
}

export function* fetchContentConfiguration({ payload }: ReturnType<typeof ConfigActions.fetchContentConfiguration>) {
  const { lang, navigate } = payload

  const i18n: i18n = yield getContext('i18n')

  try {
    yield put(
      ConfigActions.setConfigLoadingState({
        state: ConfigLoadingState.Loading
      })
    )

    const tokenResponse: AxiosResponse<{ isContractActive: boolean; isManualAccounting: boolean; token: string }> =
      yield call(apiCall, {
        umbracoCall: true,
        url: '/api/token/get',
        method: 'get',
        overrideLang: lang
      })

    const tokenResponseData = tokenResponse?.data

    // strong check for false is required because when user is not logged in, then 401 is returned
    // but tokenResponseData does not contain this status code, just only data.IsContractActive has undefined value.
    // (TODO fix apiCall so 401 status is in tokenResponseData.data)
    if (!(tokenResponseData?.isManualAccounting === false && tokenResponseData?.isContractActive === false)) {
      yield put(
        UserActions.setToken({
          token: tokenResponseData?.token || ''
        })
      )
    }

    const isLoggedIn: boolean = yield select(isUserLoggedIn)
    const preferedLanguage: string = yield select(getPreferedLanguage)
    const { checkPreferredLang } = parseGetParams(location.search)

    if (checkPreferredLang === 'Y') {
      const queryParams = new URLSearchParams(location.search)
      queryParams.delete('checkPreferredLang')
      const queryString = _.isEmpty(queryParams.toString()) ? '' : `?${queryParams.toString()}`
      history.replaceState(null, '', `${location.pathname}${queryString}`)
    }

    if (isLoggedIn && checkPreferredLang === 'Y' && lang.toLowerCase() !== preferedLanguage.toLowerCase()) {
      const transaltedPath: string = yield translateCurrentRoute(preferedLanguage)
      if (transaltedPath) {
        yield navigate({
          pathname: transaltedPath,
          search: location.search
        })
      }
    }

    const [res1, res2]: [
      AxiosResponse<{ data: ContentConfigurationResponse }>,
      AxiosResponse<{ data: BriqCommonI18n.ApiTranslations }>,
      AxiosResponse
    ] = yield all([
      call(apiCall, {
        umbracoCall: true,
        url: '/api/content/configuration',
        method: 'get',
        overrideLang: lang
      }),
      call(apiCall, {
        umbracoCall: true,
        url: '/api/content/translations',
        params: { lang }, // lang param is intended for browser to cache separately each language
        method: 'get',
        overrideLang: lang,
        headers: { ['Cache-Control']: 'max-age=86400', Pragma: '' }
      }),
      call(apiCall, {
        url: '/api/xsrf',
        method: 'get'
      })
    ])

    const contentConfig = res1?.data?.data
    const translations = res2?.data?.data

    const translationBundles = parseTranslations(translations)

    yield call(addTranslations, translationBundles, i18n)

    // strong check for false is required because when user is not logged in, then 401 is returned
    // but tokenResponseData does not contain this status code, just only data.IsContractActive has undefined value.
    // (TODO fix apiCall so 401 status is in tokenResponseData.data)
    if (tokenResponseData?.isManualAccounting === false && tokenResponseData?.isContractActive === false) {
      yield put(UserActions.detectInvalidContract())
      return
    }

    const cfg = mapContentConfigurationResponse(contentConfig || {})
    yield put(ConfigActions.setContentConfiguration({ contentConfig: cfg }))

    const registrationChannel: RegistrationChannel = yield select(getRegistrationChannel)

    if (isLoggedIn && registrationChannel === RegistrationChannel.BRQ) {
      yield spawn(checkIfUserHasToAcceptPolicies)
    }

    autoLogout.scheduleAutoLogout()
    window.addEventListener('message', (event?: Event) => {
      if ((event as any)?.origin === window.location.origin && (event as any)?.data?.name === 'aiChatbotActivity') {
        autoLogout.scheduleAutoLogout()
      }
    })

    yield put(
      ConfigActions.setConfigLoadingState({
        state: ConfigLoadingState.Loaded,
        loadedLanguage: lang
      })
    )
  } catch (e: any) {
    yield put(
      ConfigActions.setConfigLoadingState({
        state: ConfigLoadingState.Failed
      })
    )
    yield put(LogActions.addLogItem(mapActionError(e, 'ERROR', window.location.href)))
  }

  try {
    yield call(initializeTagManager)
  } catch (e: any) {
    yield put(LogActions.addLogItem(mapActionError(e, 'ERROR', window.location.href)))
  }
}

/* istanbul ignore next */
export function* configSagas() {
  yield all([takeLatest(getType(ConfigActions.fetchContentConfiguration), fetchContentConfiguration)])
}
